import { deepCamelize, deepDecamelize } from '@/lib/caseTransform'
import { useAuthStore } from '@/store/authStore'

/**
 * Outcome of a refresh attempt. Only `rejected` may end the session:
 * - `refreshed`   — the store now holds a usable token (this call rotated it,
 *                   or another tab / an earlier refresh already had).
 * - `rejected`    — the server refused the refresh token (400/401), or there
 *                   is no session left to refresh. Log out.
 * - `unreachable` — offline, network error, timeout, 5xx or an unreadable
 *                   body. The session may be fine: keep it (and the offline
 *                   queue) and retry on the next online/visibility event.
 */
type RefreshResult = 'refreshed' | 'rejected' | 'unreachable'

type Tokens = { token: string; refreshToken: string }

const REFRESH_URL = '/api/v1/auth/refresh'
const REFRESH_TIMEOUT_MS = 15_000
// Web Locks name shared by every tab/window of the origin.
const REFRESH_LOCK = 'treasurelog-refresh'

let refreshPromise: Promise<RefreshResult> | null = null

/**
 * Refresh the access token using the stored refresh token.
 *
 * Refresh tokens rotate and the backend treats a rotated-out token as theft,
 * so two refreshes with the same token must never race:
 * - within a tab, concurrent callers (a 401 retry AND `validateStoredAuth`)
 *   share one in-flight `refreshPromise`;
 * - across tabs, `navigator.locks` serializes them, and the lock holder
 *   re-reads persisted auth first so it reuses another tab's rotation
 *   instead of presenting the token that tab just spent.
 */
export function refreshAuth(): Promise<RefreshResult> {
  if (refreshPromise) return refreshPromise

  // Offline first: nothing is decided without the server, not even a session
  // that has no refresh token to present.
  if (!navigator.onLine) return Promise.resolve('unreachable')
  const sentRefreshToken = useAuthStore.getState().refreshToken
  if (!sentRefreshToken) return Promise.resolve('rejected')

  refreshPromise = withCrossTabLock(() => refreshUnderLock(sentRefreshToken)).finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}

function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  // Without Web Locks (older browsers, jsdom) only the per-tab dedupe applies.
  if (typeof navigator.locks?.request !== 'function') return fn()
  return navigator.locks.request(REFRESH_LOCK, fn)
}

async function refreshUnderLock(sentRefreshToken: string): Promise<RefreshResult> {
  // Another tab may have rotated (or logged out) while we waited for the lock.
  await useAuthStore.persist.rehydrate()
  const changed = outcomeIfChanged(sentRefreshToken)
  // The rehydrate signed this tab out without going through `logout()` (e.g.
  // the other tab's storage event never reached a bfcached page): run it so
  // this tab's caches are cleared like every other logout.
  if (changed === 'rejected') useAuthStore.getState().logout()
  if (changed) return changed

  const outcome = await postRefresh(sentRefreshToken)
  if (typeof outcome === 'string') return outcome

  // A logout (or a re-login) while the request was in flight wins: never
  // write tokens back into a session that has since gone away.
  const raced = outcomeIfChanged(sentRefreshToken)
  if (raced) return raced

  // One write, so other tabs never observe a new token paired with the spent refresh token.
  useAuthStore.setState({ token: outcome.token, refreshToken: outcome.refreshToken })
  return 'refreshed'
}

/** If the stored refresh token is no longer the one we set out with, report what replaced it. */
function outcomeIfChanged(sentRefreshToken: string): RefreshResult | null {
  const current = useAuthStore.getState().refreshToken
  if (current === sentRefreshToken) return null
  return current ? 'refreshed' : 'rejected'
}

async function postRefresh(refreshToken: string): Promise<Tokens | 'rejected' | 'unreachable'> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS)

  try {
    const response = await fetch(REFRESH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Raw fetch (bypasses `request()`), so convert case at the boundary here.
      body: JSON.stringify(deepDecamelize({ refreshToken })),
      signal: controller.signal,
    })

    // Only an explicit refusal is a dead session; any other failure is transient.
    if (response.status === 400 || response.status === 401) return 'rejected'
    if (!response.ok) return 'unreachable'

    const data = deepCamelize<Record<string, unknown> | null>(await response.json())
    const { token, refreshToken: rotated } = data ?? {}
    if (typeof token !== 'string' || !token || typeof rotated !== 'string' || !rotated) return 'unreachable'
    return { token, refreshToken: rotated }
  } catch {
    // TypeError (offline / DNS / CORS), AbortError (timeout), or a non-JSON body.
    return 'unreachable'
  } finally {
    clearTimeout(timeoutId)
  }
}
